import { Container } from 'src/components/common/Container'
import { Divider, SmallDivider } from 'src/components/common/Divider'
import styled, { css } from 'styled-components'
import React from 'react'
import {
  Noto_body1_r16,
  Noto_body2_b14,
  Noto_body2_m14,
  Noto_body2_r14,
  Noto_DisPlay2_b24,
} from 'src/styles/fonts/NotoSansKrFonts'
import { Typography } from 'src/components/common/Typography'
import { Icon } from 'src/assets/svg/Icon'
import { ReactHookFc } from 'src/components/common/ReactHookForm'
import Button, { ButtonOption3 } from 'src/components/common/Button'
import { useFormContext } from 'react-hook-form'
import { GiftRefundFormItemsType } from 'src/types/react-hook-form'
import { Roboto_num1_b20, Roboto_num1_b24 } from 'src/styles/fonts/RobotoFonts'
import { GiftRefundPageProps } from 'src/pages/GiftRefundPage/index'
import GiftHeader from 'src/components/layout/GiftHeader/view'
import { layoutMaxSize } from 'src/styles/mixin'

const GiftRefundPageView = ({
  refundSubmitRequestHandler,
  registerAttribute,
  passCertRequest,
  giftDetailData,
  percentage,
}: GiftRefundPageProps) => {
  const { SalePrice } = giftDetailData
  const { termsAgree, accountNumber, bankNum, name, pNumber } = registerAttribute
  const {
    formState: { errors, isValid = {} },
    handleSubmit,
  } = useFormContext<GiftRefundFormItemsType>()

  const {
    bankNum: { message: bankNumErrMsg } = {},
    accountNumber: { message: accountNumberErrMsg } = {},
    name: { message: nameErrMsg } = {},
    pNumber: { message: pNumberErrMsg } = {},
  } = errors

  return (
    <S.Background>
      <S.GiftRefundContainer>
        <GiftHeader title="선물받은 쿠폰 환불" isBackBtn={true} />
        <S.GiftRefundContentsContainer $option="flex_column" $row_gap="30px">
          <S.GiftRefundContextBox $option="flex_column">
            <Typography $fontStyle={Noto_DisPlay2_b24} $color="var(--black-100)">
              환불 계좌 입력
            </Typography>
            <S.GiftRefundNoticeBg $option="flex_column">
              <S.GiftRefundNoticeDesc $fontStyle={Noto_body2_r14} $color="var(--black-50)">
                ※ 쿠폰을 선물 받으신 휴대폰번호의 명의자 본인 계좌로만 환불이 가능합니다. 본인
                확인이 불가능한 경우 환불이 어려울 수 있습니다.
              </S.GiftRefundNoticeDesc>
            </S.GiftRefundNoticeBg>
            <S.GiftRefundPriceContainer $option="flex_column" $row_gap="12px">
              <S.GiftRefundPriceInfo $option="flex_row_space_between">
                <Typography $fontStyle={Noto_body1_r16} $color="var(--black-70)">
                  환불 예상 금액
                </Typography>
                <Typography $fontStyle={Roboto_num1_b20} $color="var(--black-90)">
                  {(SalePrice * percentage)?.toLocaleString()}원
                </Typography>
              </S.GiftRefundPriceInfo>
              <SmallDivider />
              <Typography $fontStyle={Noto_body2_r14} $color="var(--black-50)">
                {percentage === 1
                  ? '구매 시 실결제 금액을 기준으로 환불이 진행됩니다.'
                  : '구매 후 1년이 경과하여 결제 금액의 90%로 환불이 진행됩니다.'}
              </Typography>
            </S.GiftRefundPriceContainer>
          </S.GiftRefundContextBox>
          <Divider />
          <S.GiftRefundContextBox $option="flex_column" $row_gap="30px">
            <S.PassCertContainer $option="flex_row_space_between">
              <Typography $fontStyle={Noto_body2_r14} $color="var(--black-70)">
                본인확인 후 예금주 이름과 휴대폰번호가 자동으로 입력됩니다.
              </Typography>
              <S.PassCertButton onClick={() => passCertRequest(1, true)}>
                본인확인
                <Icon.Arrow size="16" rotate="right" color="var(--brand-50)" />
              </S.PassCertButton>
            </S.PassCertContainer>
            <Container $option="flex_column" $row_gap="20px">
              <ReactHookFc.Input
                {...name}
                style={RefundFormItemsStyle(!nameErrMsg, true)}
                topChildren={<S.GiftRefundFormTitle $text="예금주 이름" />}
                bottomChildren={<FormErrorMsg $text={nameErrMsg} />}
                disabled
              />
              <ReactHookFc.Input
                {...pNumber}
                style={RefundFormItemsStyle(!pNumberErrMsg, true)}
                topChildren={<S.GiftRefundFormTitle $text="휴대폰 번호" />}
                bottomChildren={<FormErrorMsg $text={pNumberErrMsg} />}
                disabled
              />
              <ReactHookFc.Select
                {...bankNum}
                style={RefundFormItemsStyle(!bankNumErrMsg)}
                topChildren={
                  <S.GiftRefundFormTitle $text="은행명">
                    <Icon.Arrow
                      size="16"
                      rotate="bottom"
                      color="var(--black-30)"
                      style={S.GiftRefundSelectArrow}
                    />
                  </S.GiftRefundFormTitle>
                }
                bottomChildren={<FormErrorMsg $text={bankNumErrMsg} />}
              />
              <ReactHookFc.Input
                {...accountNumber}
                style={RefundFormItemsStyle(!accountNumberErrMsg)}
                topChildren={
                  <Container $option="flex_column">
                    <S.GiftRefundFormTitle $text="계좌번호" />
                    <Typography $fontStyle={Noto_body2_r14} $color="var(--black-50)">
                      본인확인된 명의의 계좌번호를 입력해주세요.
                    </Typography>
                  </Container>
                }
                bottomChildren={<FormErrorMsg $text={accountNumberErrMsg} />}
              />
              <Container $option="flex_row_space_between" $column_gap="8px">
                <ReactHookFc.CheckBox
                  {...termsAgree}
                  bottomChildren={
                    <Container $option="flex_column" $row_gap="7px">
                      <Typography $fontStyle={Noto_body1_r16} $color={'var(--black-90)'}>
                        {termsAgree.labelValue}
                      </Typography>
                      <Container
                        $option="flex_row"
                        $style={RefundTermsDetailText}
                        onClick={termsAgree.labelOnClick}
                      >
                        <Typography $fontStyle={Noto_body2_b14} $color="var(--black-50)">
                          자세히 보기
                        </Typography>
                        <Icon.Arrow size="16" rotate="right" color="var(--black-50)" />
                      </Container>
                    </Container>
                  }
                />
              </Container>
            </Container>
          </S.GiftRefundContextBox>
        </S.GiftRefundContentsContainer>
        <S.RefundSubmitButtonWrap $option="flex_column">
          <Button
            option={ButtonOption3('active')}
            onClickFn={handleSubmit(refundSubmitRequestHandler)}
            disabled={!isValid}
          >
            제출하기
          </Button>
        </S.RefundSubmitButtonWrap>
      </S.GiftRefundContainer>
    </S.Background>
  )
}
export default GiftRefundPageView

const S = {
  Background: styled(Container)`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: var(--black-2);
  `,
  GiftRefundContainer: styled(Container)`
    ${layoutMaxSize};
    margin-top: 50px;
    align-items: center;
    padding-bottom: 68px;
    min-width: 320px;
    min-height: calc(100vh - 68px);
    background-color: var(--white);
  `,
  GiftRefundContentsContainer: styled(Container)`
    padding: 20px 0 50px;
    border-bottom: 1px solid var(--black-4);
  `,
  GiftRefundContextBox: styled(Container)`
    padding: 0 20px;
  `,
  GiftRefundPriceContainer: styled(Container)`
    margin: 10px 0;
  `,
  GiftRefundPriceInfo: styled(Container)``,
  GiftRefundSelectArrow: {
    top: '43px',
    right: '16px',
  },
  PassCertContainer: styled(Container)`
    padding: 12px 16px;
    border-radius: 12px;
    border: 1px solid var(--brand-50);
  `,
  PassCertButton: styled.button`
    ${Noto_body2_b14};
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-left: 10px;
    width: fit-content;
    gap: 2px;
    color: var(--brand-50);
    word-break: keep-all;
  `,
  GiftRefundFormTitle: styled.span<{ $text: string }>`
    ${Noto_body2_m14};
    color: var(--black-50);
    display: flex;
    justify-content: space-between;
    > span {
      position: relative;
    }
    &:before {
      content: '${(props) => props.$text}';
    }
  `,
  GiftRefundNoticeBg: styled(Container)`
    padding: 16px;
    background-color: var(--brand-5);
    margin: 20px auto;
    border-radius: 12px;
    justify-content: center;
  `,
  GiftRefundNoticeDesc: styled(Typography)`
    white-space: pre-wrap;
  `,
  GiftRefundStepNumber: styled(Typography)<{ $text?: string }>`
    display: ${(props) => (!!props.$text ? 'flex' : 'none')};
    ${Roboto_num1_b24};
    &:before {
      content: '${(props) => props.$text}';
    }
  `,
  RefundSubmitButtonWrap: styled(Container)`
    ${layoutMaxSize};
    position: fixed;
    bottom: 0;
    padding: 10px;
    min-width: 320px;
    width: 100%;
    background-color: var(--white);
    z-index: 2;
  `,
}

const RefundFormItemsStyle = (valid: boolean, disabled?: boolean) => css`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  padding: 12px 16px;
  background-color: var(--white);
  border: 1px solid var(--black-20);
  ${Noto_body1_r16};

  ${!valid &&
  css`
    background-color: var(--black-2) !important;
    border: 1px solid var(--red-50) !important;
    color: var(--black-100) !important;
  `}
  ${disabled &&
  css`
    background-color: var(--black-4);
    border: none;
    color: var(--black-40);
  `}

  &::placeholder {
    color: var(--black-40);
  }
`

const RefundTermsDetailText = css`
  align-items: center;
  width: fit-content;
  cursor: pointer;
`

const FormErrorMsg = styled.span<{ $text?: string }>`
  display: ${(props) => (!!props.$text ? 'flex' : 'none')};
  ${Noto_body2_r14};
  color: var(--red-50);
  &:before {
    content: '${(props) => props.$text}';
  }
`
